
import { Component, Vue, Watch } from "vue-property-decorator";
import FabricService from "@/services/fabric_service";
import { swatchUrl, swatchImageUrl } from "@/services/api";
import { IFabric, searchFabrics } from "@/models/fabric";

/** A dropdown is a shortcut, not a results page — past ~8 rows it's a scroll. */
const MAX_RESULTS = 8;

/**
 * Jump-to-fabric type-ahead for the app header.
 *
 * Rendered on every page EXCEPT the grid (which has its own, larger search box —
 * two search fields on one screen would just be a question of which one wins).
 * Its whole reason to exist is that the editor was previously a dead end: the
 * only ways from one fabric to the next were the browser Back button or hand-
 * editing ?code= in the address bar.
 *
 * It costs no extra request. The catalog is fetched once and cached in
 * FabricService, so this reads the same ~2k fabrics the grid and the editor's
 * brand dropdowns already pulled. Matching is the same searchFabrics() the grid
 * uses, so a term that finds a fabric there finds it here, ranked the same way.
 */
@Component
export default class HeaderSearch extends Vue {
  private fabricService = new FabricService();

  protected fabrics: IFabric[] = [];
  protected loading = false;
  protected term = "";
  protected open = false;
  /** Index of the row Enter would pick. */
  protected active = 0;

  $refs!: { input: HTMLInputElement };

  async created(): Promise<void> {
    await this.load();
    // The dropdown closes on any click that isn't ours. `mousedown`, not `click`:
    // clicking a result must not race the input's blur/close.
    document.addEventListener("mousedown", this.onDocumentMouseDown);
  }

  beforeDestroy(): void {
    document.removeEventListener("mousedown", this.onDocumentMouseDown);
  }

  private async load(): Promise<void> {
    this.loading = true;
    try {
      this.fabrics = await this.fabricService.list();
    } catch (e) {
      // Non-fatal: the header search simply finds nothing. The page the user is
      // actually on reports its own failures; this one must not shout over them.
      this.fabrics = [];
    } finally {
      this.loading = false;
    }
  }

  protected get results(): IFabric[] {
    return searchFabrics(this.fabrics, this.term).slice(0, MAX_RESULTS);
  }

  protected get overflow(): number {
    const total = searchFabrics(this.fabrics, this.term).length;
    return Math.max(0, total - MAX_RESULTS);
  }

  // Re-running the search moves the ground under the highlight; put it back on
  // the first row so Enter always picks the best match rather than whatever row
  // the old index happens to land on now.
  @Watch("term")
  protected onTermChange(): void {
    this.active = 0;
    this.open = true;
  }

  protected onFocus(): void {
    this.open = true;
    // Cheap: a cache hit is a no-op. This is what picks up a fabric created or
    // renamed since this component mounted — those writes invalidate the cache,
    // so the next read refetches.
    this.load();
  }

  protected move(delta: number): void {
    if (!this.results.length) return;
    const next = this.active + delta;
    // Clamp rather than wrap: arrowing off the end of a short list and landing
    // back at the top is disorienting when the list is this small.
    this.active = Math.min(Math.max(next, 0), this.results.length - 1);
  }

  protected choose(fabric?: IFabric): void {
    if (!fabric) return;
    const query: Record<string, string> = { code: fabric.mfr_code };
    if (fabric.name?.group) query.group = fabric.name.group;

    this.reset();
    this.$refs.input?.blur();
    // Guarded: navigating to the fabric already on screen throws
    // NavigationDuplicated, and the editor's unsaved-changes guard can also
    // cancel this — neither is an error worth surfacing.
    this.$router.push({ name: "edit", query }).catch(() => undefined);
  }

  protected reset(): void {
    this.term = "";
    this.active = 0;
    this.open = false;
  }

  protected thumb(fabric: IFabric): string {
    // Same cache-busting rule as the grid: swatch keys are derived from fabric
    // identity, so a re-uploaded image reuses its URL and would otherwise keep
    // serving the stale bytes. See FabricGrid.swatchSrc.
    const bust = fabric.assets_updated_at;
    const base = fabric.image
      ? swatchImageUrl(fabric.image, bust)
      : swatchUrl(fabric.texture_key || fabric.mfr_code, bust);
    if (!base) return "";
    return `${base}${base.includes("?") ? "&" : "?"}width=32`;
  }

  private onDocumentMouseDown(e: MouseEvent): void {
    if (!this.open) return;
    if (!this.$el.contains(e.target as Node)) this.open = false;
  }
}
